import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { guestApiClient } from '../../services/apiClient';
import { useGuestStay } from '../../hooks/useGuestStay';
import { formatCurrency } from '../../utils/currency';
import { ApiError, BookingDetails, PaymentIntentResponse } from '../../types';
import { GDTop, GDDone, GDSpinnerScreen } from './GDBits';
import { launchGatewayIntent } from './gatewayLaunch';

interface PayScreenProps {
  booking: BookingDetails;
}

type Mode = 'form' | 'confirming' | 'success';

/**
 * Pay in advance / settle the bill. Charges stay on the folio; this only
 * takes money against the outstanding balance via the hotel's gateway.
 */
const CHECKOUT_CTX_KEY = 'gd_pay_for_checkout';

const PayScreen: React.FC<PayScreenProps> = ({ booking }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { bill, billQuery, policy, currency, hotelInfo } = useGuestStay(booking);

  const [mode, setMode] = useState<Mode>('form');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paidAmount, setPaidAmount] = useState<number | null>(null);
  const [choice, setChoice] = useState<'full' | 'half' | 'other'>('full');
  const [otherAmount, setOtherAmount] = useState('');

  // Check-out payments must settle the FULL balance — no partial
  // options. Entered via /pay?for=checkout; persisted through the
  // gateway round-trip via sessionStorage; cleared on a normal visit.
  const [checkoutContext] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('for') === 'checkout') {
      sessionStorage.setItem(CHECKOUT_CTX_KEY, '1');
      return true;
    }
    if (params.get('payment_intent_id')) {
      return sessionStorage.getItem(CHECKOUT_CTX_KEY) === '1';
    }
    sessionStorage.removeItem(CHECKOUT_CTX_KEY);
    return false;
  });

  const balance = bill ? bill.balance_due : policy?.balance_due ?? 0;
  const amount =
    checkoutContext || choice === 'full' ? balance
      : choice === 'half' ? Math.round(balance * 50) / 100
        : parseFloat(otherAmount) || 0;

  // Prefill the custom-amount input with the live balance once known, so
  // switching to "Other" starts from the real outstanding amount.
  const prefilledOther = useRef(false);
  useEffect(() => {
    if (bill && !prefilledOther.current && bill.balance_due > 0) {
      prefilledOther.current = true;
      setOtherAmount(String(bill.balance_due));
    }
  }, [bill]);

  // Gateway return: ?payment_intent_id=…&status=…
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    const params = new URLSearchParams(window.location.search);
    const intentId = params.get('payment_intent_id');
    const returnStatus = params.get('status');
    if (!intentId) return;
    handled.current = true;
    window.history.replaceState({}, '', window.location.pathname);
    if (returnStatus !== 'success') {
      setError(t('gd.pay.cancelled'));
      return;
    }
    setMode('confirming');
    guestApiClient
      .get<{ success: boolean; data: any }>(`/api/guest/payment-status/${intentId}/`)
      .then(response => {
        const data = response.data.data || {};
        setPaidAmount(data.payment_data?.amount ?? null);
        setMode('success');
      })
      .catch(() => {
        setError(t('gd.pay.notCompleted'));
        setMode('form');
      })
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: ['billSummary'] });
        queryClient.invalidateQueries({ queryKey: ['preCheckInStatus'] });
      });
  }, [queryClient, t]);

  const startPayment = async () => {
    if (amount <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const response = await guestApiClient.post<{ success: boolean; data: PaymentIntentResponse }>(
        '/api/guest/create-payment-intent/',
        {
          amount,
          payment_type: 'bill_payment',
          return_url: window.location.origin + '/pay',
        }
      );
      const intent: any = (response.data as any).data || response.data;
      launchGatewayIntent(intent, {
        returnPath: '/pay',
        hotelName: hotelInfo?.hotel?.name || 'Hotel',
        description: t('gd.pay.description'),
        onDismiss: () => setBusy(false),
        onError: () => { setError(t('gd.checkin.noGateway')); setBusy(false); },
      });
    } catch (err) {
      setError((err as ApiError).error || t('gd.checkin.genericError'));
      setBusy(false);
    }
  };

  if (mode === 'confirming') {
    return <GDSpinnerScreen label={t('gd.pay.confirming')} />;
  }

  if (mode === 'success') {
    return (
      <section className="gd-screen">
        <GDDone title={t('gd.pay.successTitle')}>
          <p>
            {paidAmount
              ? t('gd.pay.successBody', { amount: formatCurrency(paidAmount, currency) })
              : t('gd.pay.successBodyNoAmount')}
          </p>
          {checkoutContext ? (
            <button
              type="button"
              className="gd-btn"
              onClick={() => {
                sessionStorage.removeItem(CHECKOUT_CTX_KEY);
                navigate('/checkout');
              }}
            >
              {t('gd.pay.continueCheckout', 'Continue check-out')}
            </button>
          ) : (
            <button type="button" className="gd-btn" onClick={() => navigate('/home')}>
              {t('gd.common.backHome')}
            </button>
          )}
        </GDDone>
      </section>
    );
  }

  return (
    <section className="gd-screen">
      <GDTop
        title={hotelInfo?.hotel?.button_labels?.['pay'] || t('gd.pay.title')}
        backTo={checkoutContext ? '/checkout' : '/home'}
      />
      <div className="gd-body">
        {error && (
          <div className="gd-note" style={{ background: 'var(--gd-red-soft)', color: 'var(--gd-red)' }}>
            {error}
          </div>
        )}

        <div className="gd-card">
          <p className="gd-muted">{t('gd.pay.balanceFor', { reference: booking.reference_number })}</p>
          <div className="gd-amount">{formatCurrency(balance, currency)}</div>
          {billQuery.isLoading && <p className="gd-muted">{t('gd.common.loading')}</p>}
          {bill && (
            <div className="gd-list" style={{ marginTop: 8, fontSize: 13 }}>
              <div className="gd-item">
                <span>{t('gd.pay.charges')}</span>
                <span style={{ marginLeft: 'auto' }}>{formatCurrency(bill.subtotal, currency)}</span>
              </div>
              <div className="gd-item">
                <span>{t('gd.pay.taxes')}</span>
                <span style={{ marginLeft: 'auto' }}>{formatCurrency(bill.tax_total, currency)}</span>
              </div>
              {bill.total_paid > 0 && (
                <div className="gd-item">
                  <span>{t('gd.pay.alreadyPaid')}</span>
                  <span style={{ marginLeft: 'auto', color: 'var(--gd-green)' }}>
                    − {formatCurrency(bill.total_paid, currency)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {policy?.prepaid_source && (
          <div className="gd-note gd-blue">
            {t('gd.checkin.prepaidVia', { source: policy.prepaid_source })}
          </div>
        )}

        {balance > 0 ? (
          <>
            {checkoutContext ? (
              <div className="gd-note gd-blue">
                {t('gd.pay.checkoutFullOnly', 'Check-out requires the full balance to be settled — the amount below is your total outstanding.')}
              </div>
            ) : (
            <div className="gd-field">
              <label>{t('gd.pay.amountLabel')}</label>
              <div className="gd-chips">
                <button
                  type="button"
                  className={`gd-chip${choice === 'full' ? ' gd-on' : ''}`}
                  onClick={() => setChoice('full')}
                >
                  {t('gd.pay.full', { amount: formatCurrency(balance, currency) })}
                </button>
                <button
                  type="button"
                  className={`gd-chip${choice === 'half' ? ' gd-on' : ''}`}
                  onClick={() => setChoice('half')}
                >
                  {t('gd.pay.half', { amount: formatCurrency(Math.round(balance * 50) / 100, currency) })}
                </button>
                <button
                  type="button"
                  className={`gd-chip${choice === 'other' ? ' gd-on' : ''}`}
                  onClick={() => setChoice('other')}
                >
                  {t('gd.pay.other')}
                </button>
              </div>
            </div>
            )}
            {choice === 'other' && (
              <div className="gd-field">
                <label>{t('gd.pay.otherLabel')}</label>
                <input
                  type="number"
                  inputMode="decimal"
                  min={1}
                  max={balance}
                  value={otherAmount}
                  onChange={e => setOtherAmount(e.target.value)}
                />
              </div>
            )}
            <button
              type="button"
              className="gd-btn"
              disabled={busy || amount <= 0 || amount > balance + 0.01}
              onClick={startPayment}
            >
              {busy
                ? t('gd.common.working')
                : t('gd.pay.payButton', { amount: formatCurrency(amount, currency) })}
            </button>
            <p className="gd-muted" style={{ textAlign: 'center' }}>{t('gd.pay.secured')}</p>
          </>
        ) : (
          <div className="gd-done-wrap" style={{ flex: 'none', padding: '14px 0 0' }}>
            <div className="gd-check">✓</div>
            <h2>{t('gd.pay.settledTitle')}</h2>
            <p>{t('gd.pay.settled')}</p>
            <button type="button" className="gd-btn gd-ghost" onClick={() => navigate('/home')}>
              {t('gd.common.backHome')}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default PayScreen;
